import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/Select';
import { Separator } from '../../../../components/Separator';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { DonationDonorInfoCollection, DonationSurveyFormData } from '../utils';

const dataCollectionNames: Record<DonationDonorInfoCollection, string> = {
	[DonationDonorInfoCollection.COLLECT]: 'Collect',
	[DonationDonorInfoCollection.REQUIRE]: 'Require',
	[DonationDonorInfoCollection.NO_COLLECT]: 'Do Not Collect',
};

const dataCollectionOptions = Object.values(DonationDonorInfoCollection).map(key => {
	return {
		label: dataCollectionNames[key as DonationDonorInfoCollection],
		value: key,
	};
});

export const DonorInformation: React.FC<{ survey: Api.IDonationSurvey }> = ({ survey }) => {
	const isArchived = !!survey?.archivedDate;
	const {
		control,
		formState: { errors },
		watch,
		setValue,
	} = useFormContext<DonationSurveyFormData>();

	const collectDonorAddress = watch('collectDonorAddress');
	const collectDonorPhone = watch('collectDonorPhone');

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Donor Information</h3>

			<FormFieldWrapper
				name='updateExistingContacts'
				control={control}
				title='Update Existing Contact Information'
				description='Overwrite existing contact data with new information from survey responses'
			>
				{({ value, onChange }) => (
					<Toggle
						id='update-existing-contacts-checkbox'
						isOn={!!value}
						onToggleCheckChanged={() => {
							const newValue = !value;
							onChange(newValue);

							if (newValue) {
								// When turning ON: If any field is "Do Not Collect", change it to "Collect"
								if (collectDonorAddress === DonationDonorInfoCollection.NO_COLLECT) {
									setValue('collectDonorAddress', DonationDonorInfoCollection.COLLECT, { shouldDirty: true });
								}
								if (collectDonorPhone === DonationDonorInfoCollection.NO_COLLECT) {
									setValue('collectDonorPhone', DonationDonorInfoCollection.COLLECT, { shouldDirty: true });
								}
							}
						}}
						uncheckedColor={grayIconFill}
						disabled={isArchived}
					/>
				)}
			</FormFieldWrapper>

			<Separator />

			<div className={css(styleSheet.formSection)}>
				<div className={css(styleSheet.formFieldSelect)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<label htmlFor={'donation-page-title-input'} className={css(styleSheet.formFieldLabel)}>
							Address
						</label>
						<span className={css(styleSheet.formFieldDescription)}>
							Select if you want to collect, require, or update existing information
						</span>
					</div>

					<div>
						<Controller
							name='collectDonorAddress'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => {
								return (
									<Select
										items={dataCollectionOptions}
										disabled={isArchived}
										value={fieldProps.value}
										onValueChange={fieldProps.onChange}
									>
										<SelectTrigger styles={[styleSheet.selectTrigger]}>
											<SelectValue placeholder='Please select option' />
										</SelectTrigger>
										<SelectContent>
											{dataCollectionOptions.map(collectionOption => (
												<SelectItem key={collectionOption.value} value={collectionOption.value}>
													{collectionOption.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								);
							}}
						/>

						{errors.collectDonorAddress?.message && (
							<span className={css(styleSheet.errorMessage)}>{errors.collectDonorAddress?.message}</span>
						)}
					</div>
				</div>
			</div>

			<Separator />

			<div className={css(styleSheet.formSection)}>
				<div className={css(styleSheet.formFieldSelect)}>
					<div className={css(styleSheet.formFieldLabelContainer)}>
						<label htmlFor={'donation-page-title-input'} className={css(styleSheet.formFieldLabel)}>
							Phone Number
						</label>
						<span className={css(styleSheet.formFieldDescription)}>
							Select if you want to collect, require, or update existing information
						</span>
					</div>

					<div>
						<Controller
							name='collectDonorPhone'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => {
								return (
									<Select
										items={dataCollectionOptions}
										disabled={isArchived}
										value={fieldProps.value}
										onValueChange={fieldProps.onChange}
									>
										<SelectTrigger styles={[styleSheet.selectTrigger]}>
											<SelectValue placeholder='Please select option' />
										</SelectTrigger>
										<SelectContent>
											{dataCollectionOptions.map(collectionOption => (
												<SelectItem key={collectionOption.value} value={collectionOption.value}>
													{collectionOption.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								);
							}}
						/>

						{errors.collectDonorPhone?.message && (
							<span className={css(styleSheet.errorMessage)}>{errors.collectDonorPhone?.message}</span>
						)}
					</div>
				</div>
			</div>

			<Separator />
		</section>
	);
};
